import { useHybridData } from "./useHybridData";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import blogIndex from "@/data/generated/blog-index.json";
import { useMemo, useState, useEffect } from "react";

// Load all generated blog post JSONs for static access
// Keys will be like '/src/data/generated/blog/slug.json'
const localPostImports = import.meta.glob('../data/generated/blog/*.json', { import: 'default' });

export interface BlogPost {
  focus_keywords: string[];
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  category: string | null;
  tags: string[] | null;
  author: string | null;
  image_url: string | null;
  hero_image_url: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  related_treatment_ids?: string[];
  related_treatment_categories?: string[];
  related_hospital_ids?: string[];
  related_doctor_ids?: string[];
  read_time_minutes?: number;
  // SEO fields (from Supabase blog_posts table)
  meta_title?: string | null;
  meta_description?: string | null;
  canonical_url?: string | null;
  featured_image?: string | null;
}

// Fetch only NEW posts since build
const fetchNewPosts = async (lastBuildDate: string) => {
  const { data, error } = await supabase
    .from("blog_posts")
    .select("*")
    .gt("published_at", lastBuildDate)
    .order("published_at", { ascending: false });

  if (error) {
    console.error("Error fetching new blog posts:", error);
    return null;
  }
  return data as BlogPost[];
};

export function useBlogPosts() {
  const { data, isStatic, isCheckingUpdates } = useHybridData<BlogPost>({
    resourceName: "Blog Posts",
    staticData: blogIndex as BlogPost[],
    fetchDynamic: fetchNewPosts,
    queryKey: ["blog_posts"],
    sortFn: (a, b) => new Date(b.published_at || 0).getTime() - new Date(a.published_at || 0).getTime(),
  });

  return { 
    data, 
    isLoading: false, // Always have static data instantly
    isStatic,
    isCheckingUpdates 
  };
}

// Simple slugify helper to match build script logic
const slugify = (text: string | null | undefined) => {
  if (!text) return '';
  return text.toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

export function useBlogPostsByCategory(categorySlug: string | undefined) {
  const { data } = useBlogPosts();

  const filtered = useMemo(() => {
    if (!categorySlug) return [];
    if (categorySlug === "All") return data;
    
    // Filter by comparing slugs
    return data?.filter((post) => slugify(post.category) === categorySlug) || [];
  }, [data, categorySlug]);

  return { 
    data: filtered, 
    isLoading: false 
  };
}

export function useBlogPost(slug: string | undefined) {
  const { data: allPosts } = useBlogPosts();
  
  // 1. Try to find in static/hybrid list (lightweight, no content likely)
  const foundInList = useMemo(() => {
    return allPosts?.find(p => p.slug === slug);
  }, [allPosts, slug]);

  const [fullPost, setFullPost] = useState<BlogPost | null>(null);
  const [isLoadingFull, setIsLoadingFull] = useState(false);

  useEffect(() => {
    if (!slug) return;

    let isMounted = true;

    const loadContent = async () => {
      // If we already have content (e.g. from previous nav), don't reload unless invalid
      // (Optional optimization, but let's keep it fresh for now)
      
      setIsLoadingFull(true);

      // A. Try Local Static JSON (Prerender/Fast Path)
      const localKey = `../data/generated/blog/${slug}.json`;
      const localLoader = localPostImports[localKey];

      if (localLoader) {
        try {
          const data = await localLoader() as BlogPost;
          if (isMounted) {
            setFullPost(data);
            setIsLoadingFull(false);
            return; // Found locally, stop here
          }
        } catch (e) {
          console.warn(`Failed to load local blog post for ${slug}`, e);
        }
      }

      // B. Fallback to Supabase (Dynamic Path)
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .single();
      
      if (isMounted) {
        if (data) {
          setFullPost(data as BlogPost);
        } else if (error) {
          console.error(`Error fetching blog post ${slug}:`, error);
        }
        setIsLoadingFull(false);
      }
    };

    loadContent();

    return () => { isMounted = false; };
  }, [slug]);

  // Merge strategy: Prefer full post. If not loaded yet, show foundInList (metadata only).
  const post = fullPost || foundInList;

  // Loading state: 
  // True if we have NO data at all (not in list, not loaded full)
  // OR if we have data but it's missing content (isCntentMissing) AND we are currently loading.
  const isCntentMissing = post && !post.content;
  
  return {
    data: post,
    isLoading: (!post && isLoadingFull) || (isCntentMissing && isLoadingFull)
  };
}
